/**
 * CondoDocumentsPanel — the Wis. Stat. § 703.33 condominium document package
 * for a transaction (arch §9.6). It replaces the old countdown banner, whose
 * "5 days … (WB-14)" text was wrong: the review window is 5 *business* days,
 * counted from delivery of the documents to the buyer's account (or, when the
 * delivery can't be confirmed, from the buyer's acknowledgement), and the
 * server computes it.
 *
 * Renders nothing when the rule doesn't apply (the API answers 404), so it is
 * safe to mount on any transaction.
 */

import React, { useMemo } from 'react';
import styles from './CondoDocumentsPanel.module.css';
import {
  CONDO_DOCUMENTS_COPY as COPY,
  CONDO_WARNING_COPY,
  CONDO_WARNING_NEEDS,
} from './condoDocumentsCopy';
import {
  NOT_STARTED_AWAITING_ACK,
  NOT_STARTED_NOT_DELIVERED,
  useCondoDocuments,
} from '../../agents/condoDocumentsAgent';
import {
  DEADLINE_KIND_AFTER_CURE,
  DEADLINE_KIND_AMENDMENT,
  ROLE_BUYER,
  ROLE_SELLER,
  clockView,
  deadlineDates,
  deadlineKind,
  deadlinePackage,
  deliverByDate,
  isAcknowledgementAnchored,
  isDelivered,
  isFirstViewAnchored,
  labelForCode,
  receiptUnconfirmedText,
  resolveViewerRole,
  sortedPackages,
} from './condoDocumentsModel';
import { formatLongLocalDate, formatLongTimestamp } from './condoDocumentsFormat';
import CondoDocumentsSellerView from './CondoDocumentsSellerView';
import CondoDocumentsBuyerView from './CondoDocumentsBuyerView';

const SEVERITY_CLASS = {
  error: styles.noticeError,
  critical: styles.noticeError,
  warning: styles.noticeWarning,
  info: styles.noticeInfo,
};

/** The delivery date a "hasn't confirmed receipt" warning is about, if the warning omits it. */
function unconfirmedDeliveredAt(packages = []) {
  const waiting = packages.filter((pkg) => isDelivered(pkg) && !pkg.buyer_received_at);
  const pkg = waiting.length ? waiting[waiting.length - 1] : null;
  return pkg?.delivered_at || null;
}

/**
 * `WarningOut.date` is a local date whose meaning depends on the code: the
 * deliver-by date (condo_docs_due_soon), the closing date (condo_docs_late,
 * condo_review_after_closing), the delivery date (condo_receipt_unconfirmed),
 * the seller's cure deadline (condo_awaiting_missing_documents) or the
 * request date (condo_missing_request_late).
 */
const WARNING_DATE_ROLE = {
  condo_docs_due_soon: 'deliverBy',
  condo_docs_late: 'closing',
  condo_review_after_closing: 'closing',
  condo_receipt_unconfirmed: 'delivered',
  condo_awaiting_missing_documents: 'cure',
  condo_missing_request_late: 'request',
};

export const WARNING_AWAITING_MISSING = 'condo_awaiting_missing_documents';
export const WARNING_MISSING_REQUEST_LATE = 'condo_missing_request_late';

/** True when the summary carries a warning with this code. */
export function hasWarning(summary, code) {
  return (summary?.warnings || []).some((w) => w.code === code);
}

/** Warning text from the copy module; the server's own message is the fallback. */
export function warningText(warning, summary, packages = []) {
  const render = CONDO_WARNING_COPY[warning.code];
  if (!render) return warning.message;
  const dateRole = WARNING_DATE_ROLE[warning.code];
  const own = (role) => (dateRole === role ? warning.date : null);
  const clock = summary?.clock || {};
  const open = clock.open_missing_request || null;
  const late = clock.late_missing_request || null;
  const values = {
    closingDate: formatLongLocalDate(own('closing') || summary?.closing_date),
    deliverByDate: formatLongLocalDate(
      warning.deliver_by_local_date || own('deliverBy') || deliverByDate(summary, warning)
    ),
    deliveredDate: own('delivered')
      ? formatLongLocalDate(own('delivered'))
      : formatLongTimestamp(warning.delivered_at || unconfirmedDeliveredAt(packages)),
    cureDate: formatLongLocalDate(own('cure') || open?.cure_deadline_local_date),
    requestDate:
      dateRole === 'request'
        ? formatLongLocalDate(warning.date || late?.requested_local_date)
        : formatLongTimestamp(open?.requested_at),
    requestByDate: formatLongLocalDate(late?.request_by_local_date),
  };
  const needs = CONDO_WARNING_NEEDS[warning.code] || [];
  if (needs.some((key) => !values[key])) return warning.message;
  if (warning.code === 'condo_receipt_unconfirmed') {
    return receiptUnconfirmedText(summary, packages, values.deliveredDate);
  }
  return render(values);
}

function WarningsBanner({ summary, packages }) {
  const warnings = summary?.warnings || [];
  if (!warnings.length) return null;
  return (
    <ul className={styles.warnings} aria-label={COPY.warningsLabel}>
      {warnings.map((warning) => (
        <li
          key={warning.code}
          className={`${styles.notice} ${SEVERITY_CLASS[warning.severity] || styles.noticeWarning}`}
          data-testid={`condo-warning-${warning.code}`}
        >
          <span className={styles.noticeIcon} aria-hidden="true">!</span>
          <span>{warningText(warning, summary, packages)}</span>
        </li>
      ))}
    </ul>
  );
}

/** The buyer's running-clock sentence, by which window it is and what started it. */
function buyerClockText(dates, { date, deliveredDate, anchorDate, timeZoneLabel }) {
  const kind = deadlineKind(dates);
  if (kind === DEADLINE_KIND_AFTER_CURE) return COPY.buyerClockAfterCure({ date, timeZoneLabel });
  if (kind === DEADLINE_KIND_AMENDMENT) {
    return COPY.buyerClockAmendment({ deliveredDate, date, timeZoneLabel });
  }
  if (isFirstViewAnchored(dates)) {
    return COPY.buyerClockFromFirstView({ date, firstViewDate: anchorDate, timeZoneLabel });
  }
  if (isAcknowledgementAnchored(dates)) {
    return COPY.buyerClockFromAcknowledgement({ date, receivedDate: anchorDate, timeZoneLabel });
  }
  return COPY.buyerClock({ date, deliveredDate, timeZoneLabel });
}

/**
 * The buyer is told to act by the earlier date when the calendar has an open
 * reading (an election day); the seller is told how late it could end.
 */
function DeadlineHedge({ isBuyer, dates, anchorDate }) {
  if (isBuyer) {
    if (!dates.buyerActBy || dates.buyerActBy === dates.primary) return null;
    const earliestDate = formatLongLocalDate(dates.buyerActBy);
    const ambiguous = dates.ambiguousDates[0];
    return (
      <p className={styles.clockHedge} data-testid="condo-clock-act-by">
        {ambiguous
          ? COPY.buyerClockAmbiguity({ ambiguousDate: formatLongLocalDate(ambiguous), earliestDate })
          : COPY.buyerActBy({ earliestDate })}
      </p>
    );
  }
  if (!dates.sellerLatest) return null;
  const latestDate = formatLongLocalDate(dates.sellerLatest);
  // The holiday reading can't move the end date: say only the receipt-date risk.
  const noHoliday = Boolean(dates.primary) && dates.sellerLatest === dates.primary;
  // Counted from the first download: a later receipt (e.g. the
  // acknowledgement) is the open question (WI.condo_docs.seller.clock_hedge_first_view).
  if (isFirstViewAnchored(dates) && anchorDate) {
    return (
      <p className={styles.clockHedge} data-testid="condo-clock-latest">
        {noHoliday
          ? COPY.sellerClockHedgeFirstViewNoHoliday({ firstViewDate: anchorDate })
          : COPY.sellerClockHedgeFirstView({ latestDate, firstViewDate: anchorDate })}
      </p>
    );
  }
  // Counted from the acknowledgement: a later receipt can't be in question,
  // only the holiday reading (WI.condo_docs.seller.clock_hedge_ack, L4).
  if (isAcknowledgementAnchored(dates)) {
    // Its only sentence is the holiday hedge; when the latest reading is the
    // same day as the calculated end there's nothing to hedge (QA 2026-10-16).
    if (noHoliday) return null;
    return (
      <p className={styles.clockHedge} data-testid="condo-clock-latest">
        {COPY.sellerClockHedgeAck({ latestDate })}
      </p>
    );
  }
  if (!anchorDate) return null;
  return (
    <p className={styles.clockHedge} data-testid="condo-clock-latest">
      {noHoliday
        ? COPY.sellerClockHedgeNoHoliday({ deliveredDate: anchorDate })
        : COPY.sellerClockHedge({ deliveredDate: anchorDate, latestDate })}
    </p>
  );
}

function ClockCard({ summary, packages, role }) {
  const view = clockView(summary, packages);
  if (view === 'hidden') return null;
  // The awaiting-missing warning already says this, with its dates: say it once.
  if (view === 'awaiting_missing' && hasWarning(summary, WARNING_AWAITING_MISSING)) return null;
  const isBuyer = role === ROLE_BUYER;
  const dates = deadlineDates(summary);
  const date = formatLongLocalDate(dates.primary);
  const anchor = deadlinePackage(summary, packages);
  // deadlineDates() already resolved this to a zone Intl accepts.
  const zone = dates.timeZone;
  const timeZoneLabel = dates.timeZoneLabel || undefined;
  const deliveredDate = formatLongTimestamp(anchor?.delivered_at, zone);
  // The instant the window was counted from (delivery, the acknowledgement,
  // or the cure): what the seller's "received later than" hedge is about.
  const anchorDate = formatLongTimestamp(dates.anchorAt || anchor?.review_anchor_at || anchor?.delivered_at, zone);

  let body;
  if (view === 'pending') {
    body = <p className={styles.clockBody} data-testid="condo-clock-pending">{COPY.clockPendingCalendar}</p>;
  } else if (view === 'awaiting_missing') {
    // Delivered, and a timely missing-documents request holds the window
    // (§ 703.33(4)(b)). WI.condo_docs.clock.awaiting_missing (L4).
    body = (
      <p className={styles.clockBody} data-testid="condo-clock-awaiting-missing">
        {COPY.clockAwaitingMissing}
      </p>
    );
  } else if (view === 'not_started') {
    // Delivered, but delivery couldn't be confirmed (legal §5.3 (a)/(d)), or
    // the rule counts from the acknowledgement: the acknowledgement starts it.
    // The documents WERE delivered here, so the "haven't been delivered"
    // sentence is only for `not_delivered` (L4 §9.4). Delivery unconfirmed:
    // the first download or the acknowledgement starts it
    // (counts_from_first_access); a rule anchored on the acknowledgement
    // alone keeps counts_from_ack.
    const reason = summary?.clock?.not_started_reason;
    let text;
    if (reason === NOT_STARTED_NOT_DELIVERED) text = COPY.clockNotStartedDelivery;
    else if (reason === NOT_STARTED_AWAITING_ACK) {
      text = isBuyer ? COPY.clockCountsFromAckBuyer : COPY.clockCountsFromAckSeller;
    } else {
      text = isBuyer ? COPY.clockCountsFromFirstAccessBuyer : COPY.clockCountsFromFirstAccessSeller;
    }
    body = (
      <div data-testid="condo-clock-not-started" data-reason={reason || undefined}>
        <p className={styles.clockBody}>{text}</p>
      </div>
    );
  } else if (view === 'ended' && isBuyer) {
    body = <p className={styles.clockBody} data-testid="condo-clock-ended">{COPY.buyerClockEnded({ date })}</p>;
  } else {
    // Running (both views), or ended for the seller: the seller's sentence
    // says "is calculated to end", and the hedge still matters afterwards.
    // The buyer's big date is the act-by date; when the calendar couldn't
    // compute it, the stored deadline without the "Act by" label.
    const bigDate = isBuyer && dates.buyerActBy
      ? COPY.actByLabel({ date: formatLongLocalDate(dates.buyerActBy) })
      : date;
    body = (
      <>
        {view === 'running' && (
          <p className={styles.clockDate} data-testid="condo-clock-date">{bigDate}</p>
        )}
        <p className={styles.clockBody} data-testid={`condo-clock-${view}`}>
          {isBuyer
            ? buyerClockText(dates, { date, deliveredDate, anchorDate, timeZoneLabel })
            : COPY.sellerClock({ date, timeZoneLabel })}
        </p>
        {!isBuyer && isFirstViewAnchored(dates) && anchorDate && (
          <p className={styles.clockBody} data-testid="condo-clock-first-view">
            {COPY.sellerClockFromFirstView({ firstViewDate: anchorDate })}
          </p>
        )}
        <DeadlineHedge isBuyer={isBuyer} dates={dates} anchorDate={anchorDate} />
      </>
    );
  }

  return (
    <section
      className={`${styles.clockCard} ${view === 'running' ? styles.clockRunning : ''}`}
      aria-labelledby="condo-clock-heading"
      data-testid="condo-clock"
    >
      <h4 id="condo-clock-heading" className={styles.clockHeading}>
        {isBuyer ? COPY.buyerDeadlineHeading : COPY.sellerDeadlineHeading}
      </h4>
      {body}
    </section>
  );
}

function ActionError({ message, detail, items, onDismiss }) {
  if (!message) return null;
  const incomplete = detail?.code === 'condo_package_incomplete';
  return (
    <div className={`${styles.notice} ${styles.noticeError}`} role="alert" data-testid="condo-action-error">
      <div>
        {incomplete ? (
          <>
            <p>{COPY.deliverIncomplete}</p>
            <ul className={styles.plainList}>
              {(detail.missing_items || []).map((code) => (
                <li key={code}>{labelForCode(code, items)}</li>
              ))}
            </ul>
          </>
        ) : (
          <p>{message}</p>
        )}
      </div>
      <button type="button" className={styles.linkButton} onClick={onDismiss}>
        {COPY.dismiss}
      </button>
    </div>
  );
}

/**
 * @param {object} props
 * @param {number|string} props.transactionId
 * @param {'seller'|'buyer'|null} props.viewerRole - used until the summary's
 *   `viewer_role` is known; null renders read-only
 */
export default function CondoDocumentsPanel({ transactionId, viewerRole }) {
  const {
    summary,
    loading,
    error,
    notApplicable,
    busy,
    actionError,
    actionErrorDetail,
    clearActionError,
    reload,
    createPackage,
    setSmallCondominium,
    uploadFile,
    removeFile,
    setItem,
    deliver,
    acknowledge,
  } = useCondoDocuments(transactionId);

  const actions = useMemo(
    () => ({ createPackage, setSmallCondominium, uploadFile, removeFile, setItem, deliver, acknowledge }),
    [createPackage, setSmallCondominium, uploadFile, removeFile, setItem, deliver, acknowledge]
  );
  const packages = useMemo(() => sortedPackages(summary), [summary]);

  if (notApplicable) return null;

  const header = (
    <header className={styles.header}>
      <span className={styles.eyebrow}>{COPY.statuteRef}</span>
      <h3 className={styles.title}>{COPY.panelTitle}</h3>
    </header>
  );

  if (loading) {
    return (
      <section className={styles.panel} data-testid="condo-documents-panel" aria-busy="true">
        {header}
        <div className={styles.loading} role="status">
          <span className={styles.spinner} aria-hidden="true" />
          <span>{COPY.loading}</span>
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className={styles.panel} data-testid="condo-documents-panel">
        {header}
        <div className={`${styles.notice} ${styles.noticeError}`} role="alert" data-testid="condo-load-error">
          <p>{COPY.loadError} {error}</p>
          <button type="button" className={styles.buttonSecondary} onClick={() => reload()}>
            {COPY.retry}
          </button>
        </div>
      </section>
    );
  }

  if (!summary) return null;

  // The server's viewer_role wins over the caller's guess ('other' is read-only).
  const role = resolveViewerRole({ viewerRole, summaryRole: summary.viewer_role });

  return (
    <section
      className={styles.panel}
      data-testid="condo-documents-panel"
      aria-labelledby="condo-documents-title"
    >
      <header className={styles.header}>
        <span className={styles.eyebrow}>{COPY.statuteRef}</span>
        <h3 id="condo-documents-title" className={styles.title}>{COPY.panelTitle}</h3>
      </header>

      <WarningsBanner summary={summary} packages={packages} />
      <ClockCard summary={summary} packages={packages} role={role} />
      <ActionError
        message={actionError}
        detail={actionErrorDetail}
        items={summary.items || []}
        onDismiss={clearActionError}
      />

      {role === ROLE_SELLER ? (
        <CondoDocumentsSellerView
          summary={summary}
          packages={packages}
          transactionId={transactionId}
          busy={busy}
          actions={actions}
        />
      ) : (
        <CondoDocumentsBuyerView
          summary={summary}
          packages={packages}
          transactionId={transactionId}
          busy={busy}
          actions={actions}
          readOnly={role !== ROLE_BUYER}
        />
      )}

      <p className={styles.disclaimer}>{COPY.disclaimer}</p>
    </section>
  );
}
